import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

const TOKEN_API = process.env.REACT_APP_ENTRI_TOKEN_API;
const ENTRI_SCRIPT_SRC = 'https://cdn.goentri.com/entri.js';
const DOMAIN_PATTERN = /^(?=.{1,253}$)(?!-)(?:[a-z0-9-]{1,63}\.)+[a-z]{2,63}$/i;

const readPrefilledDomain = (search) => {
  const domain = new URLSearchParams(search).get('domain');
  if (!domain) return undefined;
  const normalized = domain.trim().toLowerCase();
  return DOMAIN_PATTERN.test(normalized) ? normalized : undefined;
};

const loadEntri = () => {
  if (window.entri?.showUnify) {
    return Promise.resolve(window.entri);
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      if (!window.entri?.showUnify) return;
      settled = true;
      resolve(window.entri);
    };
    const fail = () => {
      if (settled) return;
      settled = true;
      reject(new Error('Could not load the Entri SDK'));
    };

    window.addEventListener('entri:ready', finish, { once: true });

    const existing = document.querySelector('script[data-entri-sdk="true"]');
    if (existing) {
      existing.addEventListener('error', fail, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = ENTRI_SCRIPT_SRC;
    script.async = true;
    script.dataset.entriSdk = 'true';
    script.addEventListener('load', finish, { once: true });
    script.addEventListener('error', fail, { once: true });
    document.body.appendChild(script);
  });
};

const fetchEntriConfig = async () => {
  if (!TOKEN_API) {
    throw new Error(
      'Domain setup is not configured. Set REACT_APP_ENTRI_TOKEN_API to the token endpoint.'
    );
  }

  const response = await fetch(TOKEN_API);
  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(
      payload.message || 'Could not get an Entri session. Try again.'
    );
  }

  if (!payload.applicationId || !payload.token || !payload.dnsRecords?.length) {
    throw new Error(
      'The token endpoint did not return an application id, token, and DNS records.'
    );
  }

  return payload;
};

const EntriDomain = () => {
  const location = useLocation();
  const prefilledDomain = readPrefilledDomain(location.search);
  const openedRef = useRef(false);
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Opening domain setup...');
  const [connectedDomain, setConnectedDomain] = useState('');

  const openModal = useCallback(async () => {
    setStatus('loading');
    setMessage('Opening domain setup...');
    setConnectedDomain('');

    const config = await fetchEntriConfig();
    const entri = await loadEntri();

    entri.showUnify({
      applicationId: config.applicationId,
      token: config.token,
      dnsRecords: config.dnsRecords,
      ...(prefilledDomain ? { prefilledDomain } : {})
    });

    setStatus('open');
    setMessage(
      'Choose whether to buy a domain or connect one you already own.'
    );
  }, [prefilledDomain]);

  useEffect(() => {
    const handlePathSelected = (event) => {
      const path = event.detail?.path;
      setMessage(
        path === 'sell'
          ? 'Buying a new domain.'
          : 'Connecting a domain you already own.'
      );
    };

    const handleSuccess = (event) => {
      const domain = event.detail?.domain || '';
      setConnectedDomain(domain);
      setStatus('success');
      setMessage(
        domain ? `${domain} is configured.` : 'The domain was configured.'
      );
    };

    const handleClose = (event) => {
      const detail = event.detail || {};
      if (detail.success) {
        const domain = detail.domain || '';
        setConnectedDomain(domain);
        setStatus('success');
        setMessage(
          domain ? `${domain} is configured.` : 'The domain was configured.'
        );
        return;
      }

      if (detail.error) {
        setStatus('error');
        setMessage(
          detail.error.title ||
            detail.error.details ||
            'Domain setup could not be completed.'
        );
        return;
      }

      setStatus('closed');
      setMessage('Domain setup was closed before it finished.');
    };

    window.addEventListener('onEntriPathSelected', handlePathSelected);
    window.addEventListener('onSuccess', handleSuccess);
    window.addEventListener('onEntriClose', handleClose);

    return () => {
      window.removeEventListener('onEntriPathSelected', handlePathSelected);
      window.removeEventListener('onSuccess', handleSuccess);
      window.removeEventListener('onEntriClose', handleClose);
    };
  }, []);

  useEffect(() => {
    if (openedRef.current) return undefined;
    openedRef.current = true;

    openModal().catch((error) => {
      setStatus('error');
      setMessage(error.message || 'Could not open domain setup.');
    });

    return undefined;
  }, [openModal]);

  const handleRetry = () => {
    openModal().catch((error) => {
      setStatus('error');
      setMessage(error.message || 'Could not open domain setup.');
    });
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      <div className="mb-8">
        <img
          src="../assets/img/adlerlocal_logo_2026.png"
          alt="Adler Local"
          className="h-10"
        />
      </div>

      <Card className="w-full max-w-md">
        <CardContent className="pt-6 text-center">
          <h1 className="text-xl font-semibold tracking-tight">
            Set up your domain
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">{message}</p>
          {connectedDomain && status === 'success' && (
            <p className="mt-2 text-sm font-medium">{connectedDomain}</p>
          )}
          {status !== 'loading' && status !== 'open' && (
            <Button
              type="button"
              className="mt-6 w-full !border-0 !bg-[#3766a8] !text-white hover:!bg-[#2d5286]"
              onClick={handleRetry}>
              {status === 'success'
                ? 'Set up another domain'
                : 'Open domain setup'}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EntriDomain;
